/**
 * buildings-layer-store — Estado de visibilidad, filtros y stats de la capa de edificios
 *
 * @remarks
 * Centraliza visibilidad, filtros V1.2 / V1.2.1 y contadores del viewport para que
 * UrbanTabbedPanel y useBuildingMapLayer compartan estado sin prop-drilling.
 *
 * V1.2.1: agrega filtros de revisión, edición manual y estado efectivo de población;
 *         agrega selectedBuildingForReview para abrir el panel de revisión React
 *         desde el click del mapa; agrega reloadTrigger para refrescar GeoJSON
 *         tras guardar/resetear una revisión.
 *
 * V1.5: agrega viewportFeatures para exponer el array de features cargados en el
 *       bbox actual al inventario edilicio del panel izquierdo.
 */

import { create } from 'zustand';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface BuildingFilters {
  qualityStatus:             'all' | 'high' | 'medium' | 'low' | 'unknown';
  requiresReview:            'all' | 'only';
  hasFootprintGeometry:      'all' | 'with' | 'without';
  morphologyClass:           'all'
    | 'small_detached_tiny'
    | 'small_detached'
    | 'medium_detached'
    | 'possible_multifamily_or_mixed'
    | 'compact_high_rise'
    | 'large_single_floor'
    | 'medium_complex'
    | 'large_high_mass_complex'
    | 'requires_use_review';
  builtMassClass:            'all' | 'minimal' | 'low' | 'medium' | 'high' | 'very_high' | 'none';
  functionalClass:           'all' | 'unclassified' | 'residential' | 'mixed' | 'non_residential';
  // V1.2.1 — revisión y estado de población efectivo
  reviewStatus:              'all' | 'reviewed' | 'unreviewed';
  manualEditStatus:          'all' | 'edited' | 'unedited';
  effectivePopulationStatus: 'all' | 'auto_eligible' | 'auto_excluded' | 'manual_included' | 'manual_excluded';
}

export interface BuildingViewportStats {
  totalLoaded:            number;
  needsReview:            number;
  excluded:               number;
  withFootprint:          number;
  withBuiltArea:          number;
  withoutFunctionalClass: number;
  manualOverrides:        number;
}

/** Edificio seleccionado para abrir el formulario de revisión React */
export interface SelectedBuildingForReview {
  buildingId:  string;
  properties:  Record<string, unknown>;
}

const DEFAULT_FILTERS: BuildingFilters = {
  qualityStatus:             'all',
  requiresReview:            'all',
  hasFootprintGeometry:      'all',
  morphologyClass:           'all',
  builtMassClass:            'all',
  functionalClass:           'all',
  reviewStatus:              'all',
  manualEditStatus:          'all',
  effectivePopulationStatus: 'all',
};

const DEFAULT_STATS: BuildingViewportStats = {
  totalLoaded:            0,
  needsReview:            0,
  excluded:               0,
  withFootprint:          0,
  withBuiltArea:          0,
  withoutFunctionalClass: 0,
  manualOverrides:        0,
};

// ─── Store ─────────────────────────────────────────────────────────────────────

interface BuildingsLayerStore {
  visible:       boolean;
  filters:       BuildingFilters;
  viewportStats: BuildingViewportStats;

  /**
   * Features cargados en el bbox actual. Poblado por useBuildingMapLayer tras cada
   * carga de GeoJSON. Permite al panel izquierdo renderizar el inventario edilicio
   * sin llamadas API adicionales.
   */
  viewportFeatures: GeoJSON.Feature[];

  /** Edificio actualmente abierto en el panel de revisión (null = cerrado) */
  selectedBuildingForReview: SelectedBuildingForReview | null;

  /**
   * Contador que incrementa cada vez que se debe recargar el GeoJSON.
   * useBuildingMapLayer observa este valor y recarga el bbox al cambiar.
   */
  reloadTrigger: number;

  toggle:               () => void;
  setVisible:           (v: boolean) => void;
  setFilters:           (f: Partial<BuildingFilters>) => void;
  resetFilters:         () => void;
  setViewportStats:     (s: BuildingViewportStats) => void;
  setViewportFeatures:  (features: GeoJSON.Feature[]) => void;
  openBuildingReview:   (buildingId: string, properties: Record<string, unknown>) => void;
  closeBuildingReview:  () => void;
  triggerReload:        () => void;
}

export const useBuildingsLayerStore = create<BuildingsLayerStore>((set) => ({
  visible:                   false,
  filters:                   DEFAULT_FILTERS,
  viewportStats:             DEFAULT_STATS,
  viewportFeatures:          [],
  selectedBuildingForReview: null,
  reloadTrigger:             0,

  toggle:              () => set((s) => ({ visible: !s.visible })),
  setVisible:          (v) => set({ visible: v }),
  setFilters:          (f) => set((s) => ({ filters: { ...s.filters, ...f } })),
  resetFilters:        () => set({ filters: DEFAULT_FILTERS }),
  setViewportStats:    (s) => set({ viewportStats: s }),
  setViewportFeatures: (features) => set({ viewportFeatures: features }),

  openBuildingReview: (buildingId, properties) =>
    set({ selectedBuildingForReview: { buildingId, properties } }),
  closeBuildingReview: () =>
    set({ selectedBuildingForReview: null }),
  triggerReload: () =>
    set((s) => ({ reloadTrigger: s.reloadTrigger + 1 })),
}));
